/**
 * Shared utilities for email template placeholder resolution.
 *
 * Used by both CustomerSendEmailModal (customers page, bulk mode) and
 * TransactionSendCopyInvoiceModal (transactions page, single mode).
 */

// ─── HTML / Variable helpers ─────────────────────────────────────────────────

export function stripHtml(html: string): string {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

export const convertVariablesToNodes = (html: string): string => {
  const normalized = html.replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, '$1');
  // Decode HTML-encoded angle brackets so both raw <<key>> and &lt;&lt;key&gt;&gt;
  // are converted to variable span nodes.
  const decoded = normalized.replace(/&lt;&lt;([^&]+)&gt;&gt;/g, '<<$1>>');
  const variableRegex = /<<([^>]+)>>/g;
  return decoded.replace(variableRegex, (match) => {
    return `<span data-variable="${match}" class="variable-node"></span>`;
  });
};

export const convertVariableNodesToText = (html: string): string => {
  return html
    .replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, (_m, v: string) =>
      v.replace(/<<([^>]+)>>/g, (_m2, k: string) => `<<${k.toLowerCase()}>>`),
    )
    .replace(/&lt;&lt;([^&]+)&gt;&gt;/g, (_m, k: string) => `<<${k.toLowerCase()}>>`)
    .replace(/<<([^>]+)>>/g, (_m, k: string) => `<<${k.toLowerCase()}>>`)
    .replace(/<\/?p>/g, '')
    .trim();
};

export const convertVariableNodesToPlaceholders = (html: string): string => {
  // First, replace any variable span nodes with the raw placeholder from data-variable
  let result = html.replace(/<span[^>]*data-variable="([^"]+)"[^>]*>[^<]*<\/span>/g, (_m, v: string) =>
    v.replace(/<<([^>]+)>>/g, (_m2, k: string) => `<<${k.toLowerCase()}>>`),
  );
  // TipTap's getHTML() HTML-encodes angle brackets in text content, so
  // <<key>> becomes &lt;&lt;key&gt;&gt;.  Decode them back so the MFE
  // receives raw <<key>> placeholders it can resolve.
  // Also lowercase the keys — the SendIt MFE uses case-insensitive matching.
  result = result.replace(/&lt;&lt;([^&]+)&gt;&gt;/g, (_m, k: string) => `<<${k.toLowerCase()}>>`);
  result = result.replace(/<<([^>]+)>>/g, (_m, k: string) => `<<${k.toLowerCase()}>>`);
  return result;
};

/** Replace all `<<key>>` placeholders in an HTML string with values from the
 *  supplied data map.  Also handles HTML-encoded variants (`&lt;&lt;key&gt;&gt;`)
 *  that TipTap may produce.  Keys that don't have a matching value are left as-is. */
export const resolvePlaceholders = (html: string, data: Record<string, string>): string => {
  if (!html) return html;
  // Resolve raw <<key>>
  let result = html.replace(/<<([^>]+)>>/g, (match, key: string) => {
    const trimmed = key.trim();
    if (trimmed in data) return data[trimmed];
    const wrapped = `<<${trimmed}>>`;
    if (wrapped in data) return data[wrapped];
    return match;
  });
  // Resolve HTML-encoded &lt;&lt;key&gt;&gt;
  result = result.replace(/&lt;&lt;([^&]+)&gt;&gt;/g, (match, key: string) => {
    const trimmed = key.trim();
    if (trimmed in data) return data[trimmed];
    return match;
  });
  return result;
};

export const isValidEmail = (email: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

/**
 * Variables that resolve to complex HTML (tables, images) and must not be used
 * in email subject lines.  Keys are lowercase for case-insensitive comparison.
 */
export const SUBJECT_DISALLOWED_VARIABLES: ReadonlySet<string> = new Set([
  'overduetransactions',
  'outstandingtransactions',
  'caseoverduetransactions',
  'caseoutstandingtransactions',
  'companylogo',
]);

/**
 * Returns the list of disallowed variable keys (with `<<>>` wrapping) found
 * in the given subject text.  Used to block saving/sending templates or emails
 * that use HTML-producing variables in the subject line.
 */
export function findDisallowedSubjectVariables(subject: string): string[] {
  const keys = extractPlaceholderKeys(subject);
  return keys.filter((key) => SUBJECT_DISALLOWED_VARIABLES.has(key.toLowerCase()));
}

/** Extract all unique `<<key>>` placeholder keys from an HTML string.
 *  Also matches HTML-encoded variants (`&lt;&lt;key&gt;&gt;`) that TipTap may produce. */
export function extractPlaceholderKeys(html: string): string[] {
  if (!html) return [];
  const keys = new Set<string>();

  // Match raw <<key>>
  const rawRegex = /<<([^>]+)>>/g;
  let match: RegExpExecArray | null;
  while ((match = rawRegex.exec(html)) !== null) {
    keys.add(match[1].trim());
  }

  // Match HTML-encoded &lt;&lt;key&gt;&gt;
  const encodedRegex = /&lt;&lt;([^&]+)&gt;&gt;/g;
  while ((match = encodedRegex.exec(html)) !== null) {
    keys.add(match[1].trim());
  }

  return [...keys];
}

/** Return placeholder keys present in the HTML that are NOT resolvable by the given merge data map. */
export function findUnresolvedPlaceholders(html: string, mergeData: Record<string, string>): string[] {
  const keys = extractPlaceholderKeys(html);
  return keys.filter((key) => !(key in mergeData));
}

/**
 * Returns placeholder keys found in the HTML that are NOT in the predefined
 * set of available template variables.  Used to hard-block saving templates
 * that contain unknown / mistyped variables.
 *
 * @param html          The template HTML (subject + body).
 * @param allowedKeys   Set of allowed inner keys (without braces), e.g. "customerName".
 */
export function findUnknownVariables(html: string, allowedKeys: Set<string>): string[] {
  const keys = extractPlaceholderKeys(html);
  return keys.filter((key) => !allowedKeys.has(key));
}

// ─── Uploaded file type ──────────────────────────────────────────────────────

export interface UploadedFile {
  name: string;
  dataUrl: string;
  contentType: string;
  base64Content: string;
}
